import type { ProductModelSource } from "./events";
import type { ProductCapture } from "./types";

/** Same caps as `isValidProductCapture` in firestore.rules. */
export const CAPTURE_LIMITS = {
  url: 2048,
  path: 256,
  triangles: 80_000,
  bytes: 12_000_000,
} as const;

export function captureSource(capture: ProductCapture | undefined): ProductModelSource {
  return capture?.source ?? "none";
}

/** `yaw` snapped to 0, 90, 180 or 270. */
export function quarterYaw(yaw: number): number {
  const quarters = Math.round((Number.isFinite(yaw) ? yaw : 0) / 90);
  return (((quarters % 4) + 4) % 4) * 90;
}

/** "Turn model": a quarter turn about the vertical axis. */
export function turnCapture(capture: ProductCapture): ProductCapture {
  return { ...capture, yaw: (quarterYaw(capture.yaw) + 90) % 360 };
}

/** Narrow untrusted data (a callable response, a Firestore doc) to a
 *  capture the products rules accept. */
export function parseCapture(value: unknown): ProductCapture | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  if (o.source !== "official" && o.source !== "generated") return null;
  if (typeof o.url !== "string" || !o.url || o.url.length > CAPTURE_LIMITS.url) return null;
  if (typeof o.path !== "string" || !o.path || o.path.length > CAPTURE_LIMITS.path) return null;
  if (!isCount(o.triangles, CAPTURE_LIMITS.triangles) || !isCount(o.bytes, CAPTURE_LIMITS.bytes)) {
    return null;
  }
  if (typeof o.pbr !== "boolean") return null;
  return {
    source: o.source,
    url: o.url,
    path: o.path,
    yaw: typeof o.yaw === "number" ? quarterYaw(o.yaw) : 0,
    triangles: o.triangles,
    bytes: o.bytes,
    pbr: o.pbr,
  };
}

function isCount(v: unknown, max: number): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= max;
}

const STORAGE_HOST = "firebasestorage.googleapis.com";
const LOCAL_HOSTS = new Set(["127.0.0.1", "localhost"]);

/**
 * Whether the 3D view may download a capture. Shared rooms carry the
 * furniture blob unvalidated, so a collaborator could otherwise point
 * every member's browser at any URL. Captures only ever live in Firebase
 * Storage (or the local emulator).
 */
export function isLoadableCaptureUrl(raw: string, allowLocal = usesEmulators()): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol === "https:" && url.hostname === STORAGE_HOST) {
    return url.pathname.startsWith("/v0/b/");
  }
  return allowLocal && url.protocol === "http:" && LOCAL_HOSTS.has(url.hostname);
}

function usesEmulators(): boolean {
  return (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) === "true";
}
